import React, { useCallback } from 'react';
import { useBoolean } from 'react-hanger';
import { NetworkRequestState } from 'c-data-layer';
import type { GridProps } from '@mui/material';
import { Box, Grid } from '@mui/material';
import type { TranslationPath } from 'c-translation';
import { Translate, useCommonTranslation } from 'c-translation';
import { HideImage } from '@mui/icons-material';
import { CollapsibleCard, ControlledFormInput, FileUploadButton, TextField } from 'c-components';
import { useDispatch, useSelector } from 'react-redux';
import { skinSelectors, skinThunks } from 'c-admin/Slices';
import type { Skin } from 'c-entity-types';
import { darkThemeOptions } from 'c-lib';
import SvgLogoWrapper from 'c-wrapper/Components/CommonWrapper/SvgLogoWrapper';
import type { SkinLogoVariant } from 'c-admin/Slices/Skin/Skin-thunks';

type Props = {
    title: TranslationPath;
    skinId: number;
    skin?: Skin;
    submitButton: React.ReactNode;
};

const UploadGridProps: GridProps['sx'] = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 200,
    justifyContent: 'space-between',
    alignItems: 'center',
};

const logoUploads: {
    variant: SkinLogoVariant;
    id: string;
    path: keyof Skin;
    label: TranslationPath;
    small: boolean;
    dark: boolean;
}[] = [
    {
        variant: 'large',
        id: 'large-logo-upload',
        path: 'logo_path',
        label: 'Modules.Admin.SkinEdit.form.largeLogoUploadLabel',
        small: false,
        dark: false,
    },
    {
        variant: 'small',
        id: 'small-logo-upload',
        path: 'logo_small_path',
        label: 'Modules.Admin.SkinEdit.form.smallLogoUploadLabel',
        small: true,
        dark: false,
    },
    {
        variant: 'largeDark',
        id: 'large-dark-logo-upload',
        path: 'logo_dark_path',
        label: 'Modules.Admin.SkinEdit.form.largeDarkLogoUploadLabel',
        small: false,
        dark: true,
    },
    {
        variant: 'smallDark',
        id: 'small-dark-logo-upload',
        path: 'logo_small_dark_path',
        label: 'Modules.Admin.SkinEdit.form.smallDarkLogoUploadLabel',
        small: true,
        dark: true,
    },
];

const SkinOverviewSection: React.FC<Props> = ({ title, skinId, skin, submitButton }) => {
    const dispatch = useDispatch();
    const open = useBoolean(true);

    const loadingStates: Record<SkinLogoVariant, NetworkRequestState> = {
        large: useSelector(skinSelectors.getUploadingLargeLogoById)(skinId).state,
        small: useSelector(skinSelectors.getUploadingSmallLogoById)(skinId).state,
        largeDark: useSelector(skinSelectors.getUploadingLargeDarkLogoById)(skinId).state,
        smallDark: useSelector(skinSelectors.getUploadingSmallDarkLogoById)(skinId).state,
    };

    const uploadLogo = useCallback(
        (file: File, variant: SkinLogoVariant) => {
            if (file != null) {
                dispatch(skinThunks.uploadLogo(skinId, file, variant));
            }
        },
        [dispatch, skinId],
    );

    const t = useCommonTranslation();

    return (
        <CollapsibleCard defaultOpen title={title}>
            <Grid container spacing={2} mb={2} p={2}>
                <Grid item xs={12} md={6} lg={3}>
                    <ControlledFormInput
                        render={({ field }) => (
                            <TextField
                                label={
                                    <Translate path="Modules.Admin.SkinsList.create.nameLabel" />
                                }
                                {...field}
                                autoFocus
                            />
                        )}
                        name="name"
                    />
                </Grid>
                <Grid
                    item
                    sx={{
                        alignSelf: 'center',
                    }}
                >
                    {submitButton}
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ height: '100%' }} p={2}>
                {logoUploads.map(logo => (
                    <Grid item key={logo.variant} sx={UploadGridProps}>
                        <Box
                            m="auto"
                            mb={1}
                            p={2}
                            borderRadius={1}
                            bgcolor={
                                logo.dark
                                    ? darkThemeOptions.palette.background.default
                                    : 'common.white'
                            }
                            color={logo.dark ? 'common.white' : 'grey.800'}
                        >
                            {skin?.[logo.path] != null ? (
                                <Box height={logo.small ? 36 : 150} display="flex">
                                    <SvgLogoWrapper
                                        small={logo.small}
                                        dark={logo.dark}
                                        skin={skin}
                                        DefaultLogo={null}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ fontSize: 100 }}>
                                    <HideImage fontSize="inherit" />
                                </Box>
                            )}
                        </Box>
                        <FileUploadButton
                            id={logo.id}
                            onFileSelected={files => uploadLogo(files[0], logo.variant)}
                            disabled={
                                loadingStates[logo.variant] === NetworkRequestState.InProgress
                            }
                        >
                            {t(logo.label)}
                        </FileUploadButton>
                    </Grid>
                ))}
            </Grid>
        </CollapsibleCard>
    );
};

export default SkinOverviewSection;
